import type { ReactNode } from 'react';
import { memo, useEffect, useState } from 'react';
import { PauseCircle, Users } from 'lucide-react';
import { Avatar, Pill, cn } from '@cyphex/ui';
import { formatDuration } from '../../lib/format';

export interface CallHeaderProps {
  /** "Ganesh Kumar" or "Ganesh Kumar and 2 others" — never a raw list. */
  title: string;
  /** Avatar seed: the first counterpart, even when the title summarises many. */
  avatarName: string;
  /** Live people on the call, me included. */
  participantCount: number;
  /** Epoch ms the media plane connected, or null while still ringing. */
  connectedAt: number | null;
  /** I put the call on hold. */
  onHold: boolean;
  /** THEY put me on hold — names, so a group call says who. */
  heldByNames: readonly string[];
  status: string;
  /**
   * The call toolbar, rendered on the RIGHT of this bar.
   *
   * Passed in rather than built here so the header stays presentational, and
   * so the toolbar keeps one owner: `CallRoom` holds every handler it needs.
   */
  actions?: ReactNode;
}

/** The thin bar above the stage: who, how long, and who is holding whom. */
export const CallHeader = memo(function CallHeader(props: CallHeaderProps) {
  const heldByOther = props.heldByNames.length > 0;

  /*
   * ONE BAR, NOT TWO.
   *
   * The toolbar used to sit along the bottom, so a call spent a strip of
   * chrome above AND below the stage. That height comes straight off the
   * shared screen: the share is bounded by the stage's HEIGHT — its width is
   * only height x aspect — so every pixel of chrome removed makes the picture
   * both taller and, at a fixed aspect ratio, wider. Teams puts the controls
   * in the top bar for the same reason.
   */
  return (
    <header className="flex items-center gap-3 border-b border-border bg-surface px-4 py-1.5">
      <Avatar name={props.avatarName} size="sm" />
      <div className="min-w-0">
        <p className="truncate text-base font-semibold text-foreground">{props.title}</p>
        {/* ONE place says what the call is doing. A status line plus a badge
            with the same words is two things to keep in sync and two things a
            screen reader reads out. */}
        <p
          className={cn(
            'truncate text-sm',
            props.onHold || heldByOther ? 'text-warning' : 'text-muted-foreground',
          )}
        >
          {props.status}
        </p>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* THE OTHER SIDE'S HOLD, which had no representation at all before.
            Being held is not the same as holding: you cannot resume it, and
            without this the screen said "Connected" while nobody could hear
            you. It is a separate mark from the status line so that a call
            where BOTH sides have pressed hold still reads correctly. */}
        {heldByOther ? (
          <Pill icon={PauseCircle} tone="warning">
            {props.heldByNames.length === 1
              ? `${props.heldByNames[0]} has you on hold`
              : `${props.heldByNames.length} people have you on hold`}
          </Pill>
        ) : null}
        <Pill icon={Users}>{String(props.participantCount)}</Pill>
        {props.connectedAt === null ? (
          <span className="tabular-nums text-base font-medium text-muted-foreground">--:--</span>
        ) : (
          <CallTimer since={props.connectedAt} />
        )}

        {/* The toolbar, if the caller gave us one. After the timer so the
            reading order is identity -> state -> actions, which is also the
            order somebody scans a call bar in. */}
        {props.actions}
      </div>
    </header>
  );
});

interface CallTimerProps {
  since: number;
}

/** Isolated so the once-a-second tick repaints six characters, not the room. */
const CallTimer = memo(function CallTimer({ since }: CallTimerProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <span className="tabular-nums text-base font-medium text-muted-foreground">
      {formatDuration(now - since)}
    </span>
  );
});
